'use client';

import React from 'react';
import { useWizard } from '@app/app/_components/Wizard/useWizard';
import { Wizard, WizardStep } from '@app/app/_components/Wizard/Wizard';
import { GatewayRedirectStep } from '@app/app/esales/transport/_sections/steps/GatewayRedirectStep';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { useBeforeUnload } from '@app/lib/use-before-unload/useBeforeUnload';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { useEffectOnce } from '@app/lib/use-change-effect/useEffectOnce';
import { clearSessionId } from '@app/infrastructure/eshop/clearSessionId';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormState } from 'react-dom';
import { scrollToTop } from '@app/lib/scroll-to-top/scrollToTop';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { Client } from '@app/domain/model/Client';
import { PersonalAccidentsInsuranceRequestResult } from '@app/service/eshop/personal-accidents/actions/requestPersonalAccidentsInsurance';
import { PurchasePersonalInsuranceResult } from '@app/service/eshop/personal-accidents/actions/purchasePersonalAccidentsInsurance';
import { createEnhancedFormAction } from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsWizardSection/helpers/createEnhancedFormAction';
import { useInsuredPersonsState } from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsWizardSection/hooks/useInsuredPersonsState';
import { usePersonalAccidentsWizardCallbacks } from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsWizardSection/hooks/usePersonalAccidentsWizardCallbacks';
import { buildSteps } from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsWizardSection/helpers/buildSteps';
import { PersonalAccidentsWizardFooter } from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsWizardSection/PersonalAccidentsWizardFooter';
import { PersonalAccidentsHiddenInputs } from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsWizardSection/PersonalAccidentsHiddenInputs';
import { PersonalAccidentsLandingSection } from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsLandingSection/PersonalAccidentsLandingSection';
import { usePriceCalculation } from '@app/app/esales/personal-accidents/_sections/setups/usePriceCalculation';
import { PersonalInsurancePremiumResponse } from '@app/domain/model/eshop/personal-accidents/PersonalInsurancePremiumResponse';
import { usePolicyholderFormFields } from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsWizardSection/hooks/usePolicyholderFormFields';
import { PA_INSURANCE_STEPS } from '@app/app/esales/personal-accidents/_sections/steps/PA_INSURANCE_STEPS';
import { GlobalLoader } from '@app/app/_components/GlobalLoader/GlobalLoader';
import { logger } from '@app/infrastructure/logger/logger';
import { ValidationError } from 'yup';
import { buildViolations } from '@app/lib/form/buildViolations';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { calculatePersonalInsurancePremiums } from '@app/service/eshop/personal-accidents/actions/calculatePersonalInsurancePremiums';
import { PurchasePersonalInsuranceSteps } from '@app/service/eshop/personal-accidents/schemas/PurchasePersonalInsuranceSteps';
import { parseLithuanianPersonalCode } from '@app/lib/personal-code/parseLithuanianPersonalCode';
import { EsalesTransportFooter } from '@app/app/esales/transport/components/EsalesTransportFooter';
import { twMerge } from 'tailwind-merge';
import { usePersonalAccidentsGtmTracking } from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsWizardSection/hooks/usePersonalAccidentsGtmTracking';
import { trackStartCalculation } from '@app/infrastructure/gtm/personal-accidents/trackStartCalculation';
import { trackProposalCalculated } from '@app/infrastructure/gtm/personal-accidents/trackProposalCalculated';

interface Props {
    backgroundImageSrc: string;
    mobileImageSrc: string;
    stakeholderLogoSrc: string;
    vigLogoSrc: string;
    authState: AuthState;
    clients: Client[];
    swedbankLogoSrc: string;
    sebLogoSrc: string;
    initialPurchaseResult?: Nullable<PurchasePersonalInsuranceResult>;
    initialStep?: Nullable<string>;
    requestData?: Nullable<PersonalAccidentsInsuranceRequestResult>;
}

export function PersonalAccidentsWizardSection({
    backgroundImageSrc,
    mobileImageSrc,
    stakeholderLogoSrc,
    vigLogoSrc,
    authState,
    clients,
    swedbankLogoSrc,
    sebLogoSrc,
    initialPurchaseResult,
    initialStep,
    requestData,
}: Props) {
    const t = useTranslations();
    const { settings } = useAppParameters();

    const isReinitializedSession = initialPurchaseResult != null && initialStep != null && requestData != null;
    const formRef = useRef<HTMLFormElement>(null);
    const setCurrentStepKeyRef = useRef<((key: string) => void) | null>(null);

    const [requestResult, setRequestResult] = useState<Nullable<PersonalAccidentsInsuranceRequestResult>>(
        requestData ?? null,
    );

    const [finished, setFinished] = useState(false);
    const [wizardStarted, setWizardStarted] = useState(isReinitializedSession);
    const [discountCodeData, setDiscountCodeData] = useState<DiscountCodeData>({
        discountCode: '',
        isInvalid: false,
        isSuccess: false,
        validTravelCode: undefined,
    });

    const [currentPremiums, setCurrentPremiums] = useState<Nullable<PersonalInsurancePremiumResponse>>(
        requestData?.premiums ?? null,
    );
    const [paymentFrequency, setPaymentFrequency] = useState(1);
    const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<Nullable<string>>(null);
    const [isRecalculating, setIsRecalculating] = useState(false);
    const [loaderText, setLoaderText] = useState('');
    const [clientValidationErrors, setClientValidationErrors] = useState<Nullable<ViolationMap>>(null);
    const [clientValidationErrorMessage, setClientValidationErrorMessage] = useState<Nullable<string>>(null);

    const {
        fields: policyholderFields,
        handlers: policyholderHandlers,
        setFields,
    } = usePolicyholderFormFields(authState);

    const {
        insuredPersons,
        handleAddPerson,
        handleRemovePerson,
        handleAgeChange,
        handleNameChange,
        handleSurnameChange,
        handlePersonalCodeChange,
        updatePrimaryPolicyholderData,
        revertPrimaryPolicyholderData,
    } = useInsuredPersonsState(initialPurchaseResult?.formData?.insuredPersons ?? requestData?.insuredPersons);

    const { priceState, handleRisksChange, initializeFromSession } = usePriceCalculation(currentPremiums);

    const { trackStepCompletion } = usePersonalAccidentsGtmTracking({
        selectedRisk: priceState.selectedRisk,
    });

    const shouldSkipInsuredPeopleStep = useMemo(() => {
        const policyholderIsInsuredPerson = policyholderFields.isInsuredPerson;

        if (!policyholderIsInsuredPerson) {
            return false;
        }

        if (insuredPersons.length > 1) {
            return false;
        }

        const hasAgeMismatch = insuredPersons.some(person => {
            if (!person.personalCode) {
                return false;
            }
            const parsedData = parseLithuanianPersonalCode(person.personalCode);
            return parsedData.isValid && parsedData.age !== undefined && parsedData.age !== person.age;
        });

        return !hasAgeMismatch;
    }, [policyholderFields.isInsuredPerson, insuredPersons]);

    const enhancedFormAction = createEnhancedFormAction(
        discountCodeData,
        insuredPersons,
        requestResult,
        priceState.selectedRisk,
        isReinitializedSession,
    );

    const [formState, formAction] = useFormState(enhancedFormAction, {
        status: FormStatus.Pending,
        data: initialPurchaseResult ?? undefined,
    });

    const {
        handleDiscountCode,
        handleApplyDiscountClick,
        handleEditPolicyholderInfo,
        handleEditInsuredPeople,
        handleEditChooseInsurance,
        handleEditBeginDate,
        onSuccessfulLogin,
    } = usePersonalAccidentsWizardCallbacks(discountCodeData, setDiscountCodeData, setCurrentStepKeyRef, formState);

    const mergedFormState = useMemo(() => {
        if (!clientValidationErrors) {
            return formState;
        }

        return {
            ...formState,
            violations: {
                ...formState.violations,
                ...clientValidationErrors,
            },
        };
    }, [formState, clientValidationErrors]);

    const handleEditLandingScreen = useCallback(() => {
        setWizardStarted(false);
        formState.status = FormStatus.Pending;
    }, [formState]);

    const onSavePolicyholder = useCallback(
        (data: typeof policyholderFields) => {
            setFields(data);
        },
        [setFields],
    );

    const handlePaymentMethodTypeChange = useCallback((paymentMethodType: Nullable<string>) => {
        setSelectedPaymentMethodType(paymentMethodType);
    }, []);

    const handlePaymentFrequencyChange = useCallback(
        async (newPaymentFrequency: number) => {
            if (!requestResult) return;

            setIsRecalculating(true);
            setLoaderText(t('eshop.personalAccidents.recalculatingPremiums'));
            setPaymentFrequency(newPaymentFrequency);

            try {
                const updatedPremiums = await calculatePersonalInsurancePremiums(
                    requestResult,
                    newPaymentFrequency.toString(),
                    formState.data?.formData,
                    priceState.selectedRiskCode,
                );

                setCurrentPremiums(updatedPremiums);
            } catch (error) {
                logger.logException(error);
                setIsRecalculating(false);
                setLoaderText('');
            } finally {
                setIsRecalculating(false);
                setLoaderText('');
            }
        },
        [requestResult, t, formState.data?.formData, priceState.selectedRiskCode],
    );

    const steps = useMemo<WizardStep[]>(
        () =>
            buildSteps({
                t,
                currentPremiums,
                selectedRiskCode: priceState.selectedRiskCode,
                selectedRisk: priceState.selectedRisk ?? null,
                totalPrice: priceState.totalPrice,
                priceToPayNow: priceState.priceToPayNow,
                handleRisksChange,
                policyholderFields,
                policyholderHandlers,
                formState: mergedFormState,
                requestResult,
                insuredPersons,
                handleNameChange,
                handleSurnameChange,
                handlePersonalCodeChange,
                onSavePolicyholder,
                editInsuredPeople: handleEditInsuredPeople,
                editPolicy: handleEditChooseInsurance,
                editBeginDate: handleEditBeginDate,
                onChangeNumberOfPersons: handleEditLandingScreen,
                editPolicyholderInfo: handleEditPolicyholderInfo,
                changeAgeOfPerson: handleEditLandingScreen,
                paymentFrequency,
                onPaymentFrequencyChange: handlePaymentFrequencyChange,
                handlePaymentMethodTypeChange,
                selectedPaymentMethodType,
                shouldSkipInsuredPeopleStep,
                authState,
                clients,
                swedbankLogoSrc,
                sebLogoSrc,
                onSuccessfulLogin,
                settings,
                initialBeginDate: formState.data?.formData?.beginDate,
            }),
        [
            t,
            currentPremiums,
            priceState.selectedRiskCode,
            priceState.selectedRisk,
            priceState.totalPrice,
            priceState.priceToPayNow,
            handleRisksChange,
            policyholderFields,
            policyholderHandlers,
            mergedFormState,
            requestResult,
            insuredPersons,
            handleNameChange,
            handleSurnameChange,
            handlePersonalCodeChange,
            onSavePolicyholder,
            handleEditInsuredPeople,
            handleEditChooseInsurance,
            handleEditBeginDate,
            handleEditLandingScreen,
            handleEditPolicyholderInfo,
            paymentFrequency,
            handlePaymentFrequencyChange,
            handlePaymentMethodTypeChange,
            selectedPaymentMethodType,
            shouldSkipInsuredPeopleStep,
            authState,
            clients,
            swedbankLogoSrc,
            sebLogoSrc,
            onSuccessfulLogin,
            settings,
            formState.data?.formData?.beginDate,
        ],
    );

    const {
        filteredSteps,
        currentSubStepIndex,
        currentStepIndex,
        currentStepKey,
        isFinalStep,
        isFirstStep,
        next,
        back,
        setCurrentStepKey,
    } = useWizard(steps);

    useEffectOnce(() => {
        const savedRiskCode =
            initialPurchaseResult?.formData?.selectedRisk?.code ??
            initialPurchaseResult?.formData?.personalInsuranceRisk;

        if (savedRiskCode) {
            initializeFromSession(savedRiskCode);
        }

        if (initialStep) {
            setCurrentStepKey(initialStep);
        }
    });

    useChangeEffect(() => {
        setCurrentStepKeyRef.current = setCurrentStepKey;
    }, [setCurrentStepKey]);

    useBeforeUnload(finished, t('general.areYouSureYouWantToLeave'));

    const handleBack = useCallback(() => {
        if (!wizardStarted) {
            confirm(t('general.areYouSureYouWantToLeave'));
        }

        if (isFirstStep && wizardStarted) {
            setWizardStarted(false);
        }

        formState.status = FormStatus.Pending;
        back();
    }, [back, formState, isFirstStep, t, wizardStarted]);

    useChangeEffect(async () => {
        if (currentStepKey === PA_INSURANCE_STEPS.CHOOSE_INSURANCE && currentPremiums?.riskPackages.length === 1) {
            try {
                if (!requestResult) return;

                setIsRecalculating(true);
                setLoaderText(t('eshop.personalAccidents.recalculatingPremiums'));

                const updatedPremiums = await calculatePersonalInsurancePremiums(
                    requestResult,
                    '1',
                    formState.data?.formData,
                );

                setCurrentPremiums(updatedPremiums ?? null);
            } catch (error) {
                logger.logException(error);
            } finally {
                setIsRecalculating(false);
                setLoaderText('');
            }
        }

        if (
            currentStepKey === PA_INSURANCE_STEPS.SUMMARY &&
            currentPremiums &&
            currentPremiums.riskPackages.length > 1 &&
            formState.data?.formData.paymentCount !== '1'
        ) {
            setClientValidationErrors(null);
            setClientValidationErrorMessage(null);

            try {
                if (!requestResult) return;

                setIsRecalculating(true);
                setLoaderText(t('eshop.personalAccidents.recalculatingPremiums'));

                const updatedPremiums = await calculatePersonalInsurancePremiums(
                    requestResult,
                    formState.data?.formData?.paymentCount,
                    formState.data?.formData,
                    priceState.selectedRiskCode,
                );

                setCurrentPremiums(updatedPremiums ?? null);
            } catch (error) {
                logger.logException(error);
            } finally {
                setIsRecalculating(false);
                setLoaderText('');
            }
        }
    }, currentStepKey);

    const handleSubmit = useCallback(async () => {
        setClientValidationErrors(null);
        setClientValidationErrorMessage(null);

        if (currentStepKey === PA_INSURANCE_STEPS.SUMMARY) {
            const formData = new FormData(formRef.current!);

            try {
                const stepSchema = PurchasePersonalInsuranceSteps[PA_INSURANCE_STEPS.SUMMARY];

                const formDataObject: Record<string, boolean> = {
                    acceptToPrivacyPolicy: formData.get('acceptToPrivacyPolicy') === 'true',
                    acceptToPaRules: formData.get('acceptToPaRules') === 'true',
                };

                await stepSchema.validate(formDataObject, { abortEarly: false });

                setIsRecalculating(true);
                setLoaderText(t('eshop.personalAccidents.preparingOffer'));

                formRef.current?.requestSubmit();
            } catch (error) {
                if (error instanceof ValidationError) {
                    const violations = buildViolations(error, 'general.validationError');
                    setClientValidationErrors(violations.violations ?? null);
                    setClientValidationErrorMessage('general.validationError');
                    scrollToTop();
                }
            }
        } else {
            formRef.current?.requestSubmit();
        }
    }, [currentStepKey, t]);

    const handleStartWizard = useCallback((result: PersonalAccidentsInsuranceRequestResult) => {
        setRequestResult(result);
        setCurrentPremiums(result.premiums);
        setWizardStarted(true);
        trackStartCalculation({
            insuredPersonsCount: result.insuredPersons.length,
        });
    }, []);

    useChangeEffect(async () => {
        if (formState.status === FormStatus.Success) {
            setIsRecalculating(false);
            setClientValidationErrors(null);
            setClientValidationErrorMessage(null);

            if (currentStepKey === PA_INSURANCE_STEPS.SUMMARY && formState.data?.offers) {
                trackProposalCalculated({
                    offers: formState.data.offers,
                    selectedRisk: priceState.selectedRisk ?? null,
                });
            }

            trackStepCompletion({ currentStepKey });

            if (isFinalStep) {
                await clearSessionId();
                setFinished(true);
            } else if (currentStepKey === PA_INSURANCE_STEPS.POLICYHOLDER) {
                if (formState.data?.formData.policyholderIsInsuredPerson) {
                    updatePrimaryPolicyholderData(
                        formState.data?.formData.policyholderFirstName || '',
                        formState.data?.formData.policyholderLastName || '',
                        requestResult?.policyHolderCode || '',
                    );
                } else {
                    revertPrimaryPolicyholderData();
                }
                next();
            } else {
                next();
            }
        }

        if (formState.status === FormStatus.Error) {
            scrollToTop();
        }

        setIsRecalculating(false);
    }, formState);

    useChangeEffect(() => {
        scrollToTop();
    }, currentStepKey);

    const getStepMarginClass = useCallback(() => {
        const stepMargins: Record<string, string> = {
            [PA_INSURANCE_STEPS.BEGIN_DATE]: '2xl:mb-[850px]',
            [PA_INSURANCE_STEPS.INSURED_PERSONS]: '2xl:mb-[700px]',
            [PA_INSURANCE_STEPS.PAYMENT.OPTIONS]: '2xl:mb-[850px]',
            [PA_INSURANCE_STEPS.PAYMENT.METHOD_TYPE]: '2xl:mb-[850px]',
        };

        return stepMargins[currentStepKey] || '';
    }, [currentStepKey]);

    if (finished) {
        return <GatewayRedirectStep checkoutResult={formState.data?.checkoutResult} />;
    }

    return (
        <>
            <GlobalLoader isVisible={isRecalculating} text={loaderText} />

            {!wizardStarted && (
                <PersonalAccidentsLandingSection
                    onSuccess={handleStartWizard}
                    backgroundImageSrc={backgroundImageSrc}
                    mobileImageSrc={mobileImageSrc}
                    stakeholderLogoSrc={stakeholderLogoSrc}
                    vigLogoSrc={vigLogoSrc}
                    insuredPersons={insuredPersons}
                    handleAddPerson={handleAddPerson}
                    handleRemovePerson={handleRemovePerson}
                    handleAgeChange={handleAgeChange}
                    discountCodeData={discountCodeData}
                    handleDiscountCode={handleDiscountCode}
                    handleApplyDiscountClick={handleApplyDiscountClick}
                    personalCode={requestResult?.policyHolderCode}
                />
            )}

            {wizardStarted && (
                <form ref={formRef} action={formAction}>
                    {!isFinalStep && <input type="hidden" name="step" value={currentStepKey} />}

                    {currentStepKey !== PA_INSURANCE_STEPS.BEGIN_DATE && (
                        <PersonalAccidentsHiddenInputs
                            beginDate={formState.data?.formData?.beginDate ?? ''}
                            endDate={formState.data?.formData?.endDate ?? ''}
                        />
                    )}

                    <Wizard
                        isPercentageStepper
                        steps={filteredSteps}
                        currentStepIndex={currentStepIndex}
                        currentSubStepIndex={currentSubStepIndex}
                        onSubmit={handleSubmit}
                        onBack={handleBack}
                        className={twMerge('mb-[400px] flex-1', getStepMarginClass())}
                        contentFooter={
                            <div className="mb-12">
                                <EsalesTransportFooter
                                    stakeholderLogoSrc={stakeholderLogoSrc}
                                    vigLogoSrc={vigLogoSrc}
                                />
                            </div>
                        }
                        error={
                            clientValidationErrorMessage
                                ? t(clientValidationErrorMessage)
                                : formState.status === FormStatus.Error
                                  ? t(formState.message)
                                  : undefined
                        }
                        footer={
                            <PersonalAccidentsWizardFooter
                                formState={formState}
                                totalPrice={priceState.totalPrice}
                                currentStepKey={currentStepKey}
                            />
                        }
                    />
                </form>
            )}
        </>
    );
}
